interface OverviewOptions {
  defaults: {
    name: string;
    selectors: {
      item: string;
    };
  };
}

export function init() {
  const elements = document.querySelectorAll<HTMLElement>('.overview');
  const defaultOptions: OverviewOptions = {
    defaults: {
      name: 'Overview Pagination',
      selectors: {
        item: '.overview__item',
      },
    },
  };

  Overview(elements, defaultOptions);
}

export default function Overview(
  elements: NodeListOf<HTMLElement>,
  options: OverviewOptions
) {
  if (process.env.NODE_ENV === 'development') {
    console.info(`Init: ${options.defaults.name}`);
  }

  for (let i = 0; i < elements.length; i++) {
    initPagination(elements[i], options);
  }
}

function initPagination(element: HTMLElement, options: OverviewOptions) {
  const grid = element.querySelector<HTMLElement>('.overview__grid');
  const items =
    grid?.querySelectorAll<HTMLElement>(options.defaults.selectors.item) || [];
  const itemsPerPage = parseInt(grid?.dataset.itemsPerPage || '6', 10);
  if (!grid || items.length === 0 || items.length <= itemsPerPage) return;
  let currentPage = 1;
  const totalPages = Math.ceil(items.length / itemsPerPage);

  const paginationContainer = document.createElement('ul');
  paginationContainer.classList.add('pagination');
  element.appendChild(paginationContainer);

  function appendPageItem(pageNumber: number) {
    const pageItem = document.createElement('li');
    pageItem.classList.add('page-item');
    pageItem.innerHTML = `
          <a href="#" class="button text" type="anchor" title="Zur ${ordinal(
      pageNumber
    )} Seite">
            ${pageNumber}
            <span class="sr-only">Zur ${ordinal(pageNumber)} Seite</span>
          </a>
        `;
    if (pageNumber === currentPage) {
      pageItem.classList.add('active');
      pageItem.querySelector('a')?.classList.add('active');
    }
    pageItem.addEventListener('click', (event) => {
      event.preventDefault();
      goToPage(pageNumber);
    });
    paginationContainer.appendChild(pageItem);
  }

  function updatePagination() {
    paginationContainer.innerHTML = '';

    const prevPage = document.createElement('li');
    prevPage.classList.add('prev-page');
    prevPage.innerHTML = `
          <a href="#" class="button primary with-icon icon-only" type="anchor" title="Zur vorherigen Seite" aria-disabled="${currentPage === 1
      }">
            <svg fill="none" xmlns="http://www.w3.org/2000/svg" width="20" height="20">
                <path d="M18.2608 10.0001C18.2608 10.4784 17.8694 10.8697 17.3912 10.8697L4.69554 10.8697L8.63032 14.8262C8.97814 15.1741 8.97814 15.7175 8.63032 16.0654C8.47814 16.2175 8.26075 16.3045 8.02162 16.3045C7.80423 16.3045 7.5651 16.2175 7.41293 16.0436L1.99988 10.6088C1.65206 10.261 1.65206 9.71754 1.99988 9.39145L7.41293 3.95667C7.76075 3.60884 8.30423 3.60884 8.65206 3.95667C8.99988 4.3045 8.99988 4.84797 8.65206 5.1958L4.69554 9.13058L17.3912 9.13058C17.8694 9.13058 18.2608 9.52189 18.2608 10.0001Z" fill="currentColor"/>
            </svg>
            <span class="sr-only">Zur vorherigen Seite</span>
          </a>
        `;
    prevPage.addEventListener('click', (event) => {
      event.preventDefault();
      if (currentPage > 1) {
        goToPage(currentPage - 1);
      }
    });
    paginationContainer.appendChild(prevPage);

    let startPage = 1;
    let endPage = totalPages;

    if (totalPages > 5) {
      if (currentPage <= 3) {
        startPage = 1;
        endPage = 4;
      } else if (currentPage > totalPages - 3) {
        startPage = totalPages - 3;
        endPage = totalPages;
      } else {
        startPage = currentPage - 2;
        endPage = currentPage + 1;
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      appendPageItem(i);
    }

    if (endPage < totalPages) {
      const ellipsisItem = document.createElement('li');
      ellipsisItem.classList.add('page-item');
      ellipsisItem.innerHTML = `
              <span class="button text" type="anchor" title="Mehr Seiten">
                ...
                <span class="sr-only">Mehr Seiten</span>
              </span>
            `;
      paginationContainer.appendChild(ellipsisItem);
      appendPageItem(totalPages);
    }

    const nextPage = document.createElement('li');
    nextPage.classList.add('next-page');
    nextPage.innerHTML = `
          <a href="#" class="button primary with-icon icon-only" type="anchor" title="Zur nächsten Seite" aria-disabled="${currentPage === totalPages
      }">
            <svg fill="none" xmlns="http://www.w3.org/2000/svg" height="20" width="20">
                <path d="M17.9999 10.6087L12.5868 16.0435C12.4129 16.2174 12.1955 16.3044 11.9781 16.3044C11.7608 16.3044 11.5434 16.2174 11.3694 16.0435C11.0216 15.6957 11.0216 15.1522 11.3694 14.8044L15.3042 10.8696L2.60858 10.8696C2.13032 10.8696 1.73901 10.4783 1.73901 10C1.73901 9.52175 2.13032 9.13044 2.60858 9.13044L15.3042 9.13044L11.3477 5.17392C10.9999 4.82609 10.9999 4.28262 11.3477 3.93479C11.6955 3.58696 12.239 3.58696 12.5868 3.93479L17.9999 9.36957C18.3477 9.7174 18.3477 10.2826 17.9999 10.6087Z" fill="currentColor"/>
            </svg>
            <span class="sr-only">Zur nächsten Seite</span>
          </a>
        `;
    nextPage.addEventListener('click', (event) => {
      event.preventDefault();
      if (currentPage < totalPages) {
        goToPage(currentPage + 1);
      }
    });
    paginationContainer.appendChild(nextPage);
  }

  function goToPage(page: number) {
    currentPage = page;
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;

    items.forEach((item, index) => {
      if (index >= start && index < end) {
        item.style.display = '';
      } else {
        item.style.display = 'none';
      }
    });

    updatePagination();
  }

  goToPage(1);
  console.log('[initialized] overview.ts');
}

function ordinal(num: number): string {
  const ordinals = [
    'ersten',
    'zweiten',
    'dritten',
    'vierten',
    'fünften',
    'sechsten',
    'siebten',
    'achten',
    'neunten',
    'zehnten',
    'elften',
    'zwölften',
    'dreizehnten',
    'vierzehnten',
    'fünfzehnten',
    'sechzehnten',
    'siebzehnten',
    'achtzehnten',
    'neunzehnten',
    'zwanzigsten'
  ];
  return ordinals[num - 1] || `${num}.`;
}
